/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, RefreshCw, Download, Filter,
  DollarSign, Percent, CheckCircle2, Activity, BarChart2,
  Users, Receipt,
} from 'lucide-react';
import {
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import CountUp from 'react-countup';
// import {
//   kpiMetrics, revenueData, productRevenue, topTenantsByRevenue,
//   tenantGrowthData, transactionTrend, filterOptions,
//   recentTransactions, recentInvoices, topProducts,
//   sparklineRevenue, sparklineArr, sparklineTenants, sparklineSuccess,
//   sparklineTxn, sparklineCollection,
// } from '../data/mockDashboardData';
import { getDashboradChartsDetails } from "../Actions/dashboardChartActions";
import { useEffect } from 'react';
import { decryptedData } from "../utils/encryptDecrypt"

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (v, symbol = "CI$") => {
  if (v >= 1_000_000_000_000) return `${symbol} ${(v / 1_000_000_000_000).toFixed(2)}T`;
  if (v >= 1_000_000_000) return `${symbol} ${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `${symbol} ${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${symbol} ${(v / 1_000).toFixed(1)}K`;

  return `${symbol} ${v}`;
};

// ─── Sparkline ───────────────────────────────────────────────────────────────
const Sparkline = ({
  data = [],
  color = "#fff",
  height = 42,
}) => {

  if (
    !Array.isArray(data) ||
    data.length < 2
  ) {
    return null;
  }

  const width = 140;
  const padding = 4;

  const values = data.map(Number);

  const max = Math.max(...values);
  const min = Math.min(...values);

  const range =
    max - min || 1;

  const points = values.map(
    (value, index) => {

      const x =
        (index / (values.length - 1))
        * width;

      const y =
        height -
        (
          ((value - min) / range)
          * (height - padding * 2)
        ) -
        padding;

      return [x, y];
    }
  );

  // smooth curved line
  const linePath = points.reduce(
    (acc, point, i, arr) => {

      if (i === 0) {
        return `M ${point[0]} ${point[1]}`;
      }

      const prev = arr[i - 1];

      const cx =
        (prev[0] + point[0]) / 2;

      return `
        ${acc}
        Q ${cx} ${prev[1]}
        ${point[0]} ${point[1]}
      `;
    },
    ""
  );

  // area path
  const areaPath = `
    ${linePath}
    L ${width} ${height}
    L 0 ${height}
    Z
  `;

  const gradientId =
    `spark-gradient-${color.replace("#", "")}`;

  return (

    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >

      <defs>

        <linearGradient
          id={gradientId}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop
            offset="0%"
            stopColor={color}
            stopOpacity="0.45"
          />

          <stop
            offset="100%"
            stopColor={color}
            stopOpacity="0"
          />
        </linearGradient>

      </defs>

      {/* AREA */}
      <path
        d={areaPath}
        fill={`url(#${gradientId})`}
      />

      {/* LINE */}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

    </svg>
  );
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ title, value, trend, isUp, prefix, suffix, gradient, iconBg, icon: Icon, sparkData, sparkColor }) => (
  <div style={{
    borderRadius: 16, padding: '18px 20px 14px',
    background: gradient || '#fff',
    border: gradient ? 'none' : '1px solid #e2e8f0',
    boxShadow: gradient ? '0 8px 24px rgba(99,102,241,0.18)' : '0 1px 4px rgba(0,0,0,0.04)',
    position: 'relative', overflow: 'hidden', flex: 1,
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'default',
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = gradient ? '0 12px 32px rgba(99,102,241,0.25)' : '0 6px 20px rgba(0,0,0,0.08)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = gradient ? '0 8px 24px rgba(99,102,241,0.18)' : '0 1px 4px rgba(0,0,0,0.04)'; }}
  >
    {gradient && (
      <div style={{
        position: 'absolute', top: -24, right: -24, width: 100, height: 100,
        borderRadius: '50%', background: 'rgba(255,255,255,0.12)',
      }} />
    )}
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
      <div>
        <div style={{ fontSize: 11.5, fontWeight: 500, color: gradient ? 'rgba(255,255,255,0.85)' : '#64748b', marginBottom: 4 }}>
          {title}
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, color: gradient ? '#fff' : '#0f172a', lineHeight: 1.2 }}>
          {prefix}<CountUp end={typeof value === 'string' ? parseFloat(value) : value} decimals={value >= 1000000 ? 2 : 0} duration={1.5} separator="," />{suffix}
        </div>
      </div>
      <div style={{
        width: 40, height: 40, borderRadius: 12, flexShrink: 0,
        background: gradient ? 'rgba(255,255,255,0.22)' : (iconBg || '#f1f5f9'),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={20} color={gradient ? '#fff' : (iconBg ? '#fff' : '#6366f1')} />
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
      {isUp
        ? <TrendingUp size={13} color={gradient ? 'rgba(255,255,255,0.9)' : '#22c55e'} />
        : <TrendingDown size={13} color={gradient ? 'rgba(255,255,255,0.9)' : '#ef4444'} />}
      <span style={{ fontSize: 11.5, fontWeight: 600, color: gradient ? 'rgba(255,255,255,0.9)' : (isUp ? '#22c55e' : '#ef4444') }}>
        {isUp ? '+' : ''}{trend}%
      </span>
      <span style={{ fontSize: 11, color: gradient ? 'rgba(255,255,255,0.7)' : '#94a3b8' }}>
        vs Apr 1 – Apr 30
      </span>
    </div>
    <Sparkline data={sparkData} color={sparkColor} isGradientCard={!!gradient} />
  </div>
);

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionTitle = ({ children, action }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
    <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{children}</span>
    {action && <span style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', cursor: 'pointer' }}>{action}</span>}
  </div>
);

// ─── Card wrapper ─────────────────────────────────────────────────────────────
const Panel = ({ children, style }) => (
  <div style={{
    background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)', padding: '18px 20px',
    ...style,
  }}>
    {children}
  </div>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────
const Badge = ({ status }) => {
  const map = {
    success: { bg: '#dcfce7', color: '#16a34a', label: 'Success' },
    paid: { bg: '#dcfce7', color: '#16a34a', label: 'Paid' },
    failed: { bg: '#fee2e2', color: '#dc2626', label: 'Failed' },
    overdue: { bg: '#fee2e2', color: '#dc2626', label: 'Overdue' },
    pending: { bg: '#fef9c3', color: '#ca8a04', label: 'Pending' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const ChartTip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0f172a', borderRadius: 10, padding: '10px 14px',
      border: '1px solid rgba(255,255,255,0.1)', fontSize: 12, color: '#f8fafc',
    }}>
      <div style={{ marginBottom: 6, fontWeight: 600, color: '#94a3b8' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          <span style={{ color: '#cbd5e1' }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{currency ? fmt(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

const ChartTipForGrowth = ({ active, payload, currency }) => {
  if (!active || !payload?.length) return null;

  const month = payload?.[0]?.payload?.month;

  return (
    <div
      style={{
        background: "#0f172a",
        borderRadius: 10,
        padding: "10px 14px",
        border: "1px solid rgba(255,255,255,0.1)",
        fontSize: 12,
        color: "#f8fafc",
      }}
    >
      <div
        style={{
          marginBottom: 6,
          fontWeight: 600,
          color: "#94a3b8",
        }}
      >
        {month}
      </div>

      {payload?.map((p, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 3,
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: p?.color,
            }}
          />
          <span style={{ color: "#cbd5e1" }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>
            {currency ? fmt(p?.value) : p?.value}
          </span>
        </div>
      ))}

      {/* {payload?.[0]?.payload?.newTenants !== undefined && (
        <div
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            color: "#22c55e",
            fontWeight: 600,
          }}
        >
          New Tenants: {payload[0]?.payload?.newTenants}
        </div>
      )} */}
    </div>
  );
};

const convertMonthlyToQuarterly = (
  data
) => {

  const quarters = {};

  data.forEach((item, index) => {

    const quarter =
      `Q${Math.floor(index / 3) + 1}`;

    if (!quarters[quarter]) {
      quarters[quarter] = {
        month: quarter,
        revenue: 0,
        prevYear: 0,
      };
    }

    quarters[quarter].revenue +=
      Number(item.revenue || 0);

    quarters[quarter].prevYear +=
      Number(item.prevYear || 0);
  });

  return Object.values(quarters);
};


const filterOptions = {
  dateRanges: [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'Last 15 Days', value: 'last15days' },
    // { label: 'Last 30 Days', value: 'last30days' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'This Quarter', value: 'thisQuarter' },
    { label: 'Last 6 Months', value: 'last6months' },
    { label: 'Custom Range', value: 'custom' },
  ],
  products: [
    { label: 'QBot Portal', value: 'qbot' },
    { label: 'PayCentral', value: 'paycentral' },
    { label: 'KYC Portal', value: 'kyc' },
    { label: 'Tripolaa Portal', value: 'tripola' },
  ],
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const MasterDashboard = () => {
  const [dateRange, setDateRange] = useState('last6months');
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const [product, setProduct] = useState('');
  const [tenant, setTenant] = useState('');
  const [shouldFetchDetails, setShouldFetchDetails] = useState(true);

  //  dashboard Data
  const [dashbaordData, setDashboardData] = useState({});

  // tenant growth data states
  const [growthData, setGrowthData] = useState([]);
  const [growthDataStates, setGrowthDataStates] = useState([]);

  const [recentTransactionsData, setRecentTransactionsData] = useState([]);
  const [topProductsData, setTopProductsData] = useState([]);

  // revenue trend chart data
  const [revenueTrendData, setRevenueTrendData] = useState([]);
  // revenue by product pie chart data
  const [revenueByProductData, setRevenueByProductData] = useState([]);
  const [totalRevenueByProductCountData, setTotalRevenueByProductCountData] = useState([]);

  // top 10 tenants by revenue data
  const [topTenantsByRevenueData, setTopTenantsByRevenueData] = useState([]);

  // MRR Reports Data
  const [mrrData, setMrrData] = useState([]);

  //Recent Invoices
  const [recentInvoicesData, setRecentInvoicesData] = useState([]);

  //Invoice Status 
  const [invoiceStatusData, setInvoiceStatusData] = useState({});

  //kpicard data
  const [kpiData, setKpiData] = useState({})

  //Revenuetrend graph state 
  const [revenueViewType, setRevenueViewType] = useState("monthly");

  //all tenant data
  const [allTenantsData, setAllTenantsData] = useState([]);
  const filteredTenants = product ? allTenantsData.filter((t) => t.portalType?.toLowerCase()?.trim() === product?.toLowerCase()?.trim()) : allTenantsData;

  // useEffect(() => {
  //   fetchDashboardData();
  // }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [product, tenant, dateRange, startDate, endDate]);


  const handleResetFilters = () => {
    setDateRange("last6months");
    setProduct("");
    setTenant("");
    // fetchDashboardData();
  };

  const handleProductChange = (value) => {
    setProduct(value);
    setTenant("");
  };

  const fetchDashboardData = () => {
    getDashboradChartsDetails(product, tenant, dateRange, startDate, endDate)
      .then((res) => {
        console.log("response", res);
        setDashboardData(res?.data || {});
        setGrowthData(res?.data?.data?.tenantGrowthData?.tenantGrowthData || []);
        setGrowthDataStates(res?.data?.data?.tenantGrowthData || []);
        setRecentTransactionsData(res?.data?.data?.recentTransactionData || []);
        setTopProductsData(res?.data?.data?.topProductsRevenueData || []);
        setRevenueTrendData(res?.data?.data?.productRevenueData || []);
        setRevenueByProductData(res?.data?.data?.revenueByProductData?.productRevenue || []);
        setTotalRevenueByProductCountData(res?.data?.data?.revenueByProductData?.totalRevenue || []);
        setTopTenantsByRevenueData(res?.data?.data?.topTenantByRevenueData || []);
        setMrrData(res?.data?.data?.MRROverviewData || []);
        setRecentInvoicesData(res?.data?.data?.RecentInvoices || []);
        setInvoiceStatusData(res?.data?.data?.InvoiceStatusData || [])
        setKpiData(res?.data?.data?.KPIMetricsData || {})
        setAllTenantsData(res?.data?.data?.AllTenantData || []);
      })
      .catch((error) => {
        console.log("error", error);
      });
  };
  // console.log(allTenantsData);
  // console.log(product);

  const maxRev = Math.max(
    ...(topTenantsByRevenueData?.map(item => Number(item.revenue)) || [1])
  );
  const DONUT_COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#f59e0b'];

  const sel = {
    height: 34, fontSize: 12, padding: '0 10px', borderRadius: 8,
    border: '1px solid #e2e8f0', background: '#fff', color: '#475569',
    cursor: 'pointer', outline: 'none', appearance: 'none',
    paddingRight: 28,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
  };

  const revenueChartData =
    revenueViewType === "quarterly"
      ? convertMonthlyToQuarterly(
        revenueTrendData
      )
      : revenueTrendData;

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter',system-ui,sans-serif" }}>

      {/* ── Page Body ──────────────────────────────────────────────────── */}
      <main style={{ padding: '20px 24px', flex: 1 }}>

        {/* ── Filter Bar ───────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 8, marginBottom: 18,
          background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
          padding: '10px 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select value={dateRange} onChange={e => setDateRange(e.target.value)} style={sel}>
              {filterOptions.dateRanges.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            {dateRange === "custom" && (
              <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>

                {/* Start Date */}
                <input
                  type="date"
                  value={startDate}
                  max={today}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={sel}
                />

                {/* End Date */}
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  max={today}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={sel}
                />

              </div>
            )}
            <select value={product} onChange={e => handleProductChange(e.target.value)} style={sel}>
              <option value="">All Products</option>
              {filterOptions.products.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <select
              value={tenant}
              onChange={e => setTenant(e.target.value)}
              style={sel}
            >
              <option value="">
                All Tenants
              </option>

              {filteredTenants.map(t => (
                <option
                  key={t.portalId}
                  value={t.portalId}
                >
                  {(t.tenantName)}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={handleResetFilters}
                style={{
                  ...sel,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '0 10px',
                  backgroundImage: 'none',
                  paddingRight: '10px'
                }}
              >
                <RefreshCw size={13} />
              </button>
            </div>
          </div>

        </div>

        {/* ── Row 1: KPI Cards ─────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 18 }}>

          <KpiCard
            title="Monthly Revenue"
            value={kpiData?.monthlyRevenue || 0}
            prefix="$"
            suffix=""
            trend={kpiData?.monthlyRevenueTrend || 0}
            isUp={(kpiData?.monthlyRevenueTrend || 0) >= 0}
            icon={DollarSign}
            gradient="linear-gradient(135deg,#6366f1,#7c3aed)"
            sparkData={kpiData?.monthlyRevenueSpark || []}
            sparkColor="#fff"
          />

          <KpiCard
            title="ARR"
            value={kpiData?.arr || 0}
            prefix="$"
            suffix=""
            trend={kpiData?.arrTrend || 0}
            isUp={(kpiData?.arrTrend || 0) >= 0}
            icon={TrendingUp}
            gradient="linear-gradient(135deg,#3b82f6,#06b6d4)"
            sparkData={kpiData?.arrSpark || []}
            sparkColor="#fff"
          />

          <KpiCard
            title="Active Tenants"
            value={kpiData?.activeTenants || 0}
            prefix=""
            suffix=""
            trend={kpiData?.activeTenantsTrend || 0}
            isUp={(kpiData?.activeTenantsTrend || 0) >= 0}
            icon={Users}
            iconBg="#6366f1"
            sparkData={kpiData?.activeTenantsSpark || []}
            sparkColor="#6366f1"
          />

          <KpiCard
            title="Success Rate"
            value={kpiData?.successRate || 0}
            prefix=""
            suffix="%"
            trend={kpiData?.successRateTrend || 0}
            isUp={(kpiData?.successRateTrend || 0) >= 0}
            icon={CheckCircle2}
            iconBg="#22c55e"
            sparkData={kpiData?.successRateSpark || []}
            sparkColor="#22c55e"
          />

        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 18 }}>

          <KpiCard
            title="Total Transactions"
            value={kpiData?.totalTransactions || 0}
            prefix=""
            suffix=""
            trend={kpiData?.totalTransactionsTrend || 0}
            isUp={(kpiData?.totalTransactionsTrend || 0) >= 0}
            icon={Activity}
            iconBg="#06b6d4"
            sparkData={kpiData?.totalTransactionsSpark || []}
            sparkColor="#06b6d4"
          />

          <KpiCard
            title="Pending Invoices"
            value={kpiData?.pendingInvoices || 0}
            prefix=""
            suffix=""
            trend={kpiData?.pendingInvoicesTrend || 0}
            isUp={(kpiData?.pendingInvoicesTrend || 0) >= 0}
            icon={Receipt}
            iconBg="#f59e0b"
            sparkData={kpiData?.pendingInvoicesSpark || []}
            sparkColor="#f59e0b"
          />

          <KpiCard
            title="Collection Rate"
            value={kpiData?.collectionRate || 0}
            prefix=""
            suffix="%"
            trend={kpiData?.collectionRateTrend || 0}
            isUp={(kpiData?.collectionRateTrend || 0) >= 0}
            icon={Percent}
            iconBg="#22c55e"
            sparkData={kpiData?.collectionRateSpark || []}
            sparkColor="#22c55e"
          />

          <KpiCard
            title="Revenue Growth"
            value={kpiData?.revenueGrowth || 0}
            prefix=""
            suffix="%"
            trend={kpiData?.revenueGrowthTrend || 0}
            isUp={(kpiData?.revenueGrowthTrend || 0) >= 0}
            icon={BarChart2}
            iconBg="#8b5cf6"
            sparkData={kpiData?.revenueGrowthSpark || []}
            sparkColor="#8b5cf6"
          />

        </div>

        {/* ── Row 2: Primary Charts ─────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px 260px', gap: 14, marginBottom: 18 }}>

          {/* Revenue Trend */}
          <Panel>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Revenue Trend ({revenueViewType === "monthly" ? "Last 12 Months" : "Quarterly"})</div>
                <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
                    <span style={{ display: 'inline-block', width: 24, height: 2, background: '#6366f1', borderRadius: 2 }} />
                    Revenue (₹)
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
                    <span style={{ display: 'inline-block', width: 24, height: 2, background: '#cbd5e1', borderRadius: 2, borderBottom: '2px dashed #94a3b8' }} />
                    Previous Year (₹)
                  </span>
                </div>
              </div>
              <select
                value={revenueViewType}
                onChange={(e) =>
                  setRevenueViewType(e.target.value)
                }
                style={{
                  height: 28,
                  fontSize: 11,
                  padding: '0 20px 0 8px',
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                  background: '#f8fafc',
                  color: '#475569',
                  appearance: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="monthly">
                  Monthly
                </option>

                <option value="quarterly">
                  Quarterly
                </option>
              </select>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={revenueChartData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.22} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={52} />
                <Tooltip content={<ChartTip currency />} />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#6366f1" strokeWidth={2.5}
                  fill="url(#gRev)" dot={false} activeDot={{ r: 5, fill: '#6366f1', stroke: '#fff', strokeWidth: 2 }} />
                <Line type="monotone" dataKey="prevYear" name="Prev Year" stroke="#cbd5e1"
                  strokeWidth={2} strokeDasharray="5 4" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          {/* Revenue by Product */}
          <Panel style={{ display: 'flex', flexDirection: 'column' }}>
            <SectionTitle>Revenue by Product</SectionTitle>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={revenueByProductData} cx="50%" cy="50%" innerRadius={52} outerRadius={74}
                    dataKey="revenue" startAngle={90} endAngle={-270}>
                    {revenueByProductData?.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i]} />)}
                  </Pie>
                  <Tooltip content={<ChartTip currency />} />
                </PieChart>
              </ResponsiveContainer>
              {/* Centre label */}
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{fmt(totalRevenueByProductCountData)}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>Total</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 6 }}>
              {revenueByProductData?.map((p, i) => (
                <div key={p.product} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#475569' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: DONUT_COLORS[i], flexShrink: 0 }} />
                    {p.product}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{p.percentage}% ({fmt(p.revenue)})</span>
                </div>
              ))}
            </div>
          </Panel>

          {/* Top 10 Tenants */}
          <Panel style={{ display: 'flex', flexDirection: 'column' }}>
            <SectionTitle>Top 10 Tenants by Revenue</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
              {topTenantsByRevenueData?.map((t, i) => (
                <div key={t.tenantName}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 11, color: '#475569', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.tenantName}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a' }}>{fmt(t.revenue)}</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 4, width: `${(t.revenue / maxRev) * 100}%`,
                      background: `linear-gradient(90deg,#6366f1,#8b5cf6)`
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* ── Row 3: Secondary Analytics ───────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 18 }}>
          {/* Tenant Growth */}
          <Panel>
            <SectionTitle>Tenant Growth</SectionTitle>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{growthDataStates?.totalTenants}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>Total Tenants</div>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#22c55e' }}>↑ {growthDataStates?.currentMonthNewTenants} this month</span>
            <ResponsiveContainer width="100%" height={70}>
              <AreaChart data={growthData} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gTen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip content={<ChartTipForGrowth />} />
                <Area type="monotone" dataKey="tenants" name="Tenants" stroke="#8b5cf6" strokeWidth={2.5}
                  fill="url(#gTen)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          {/* Invoice Status */}
          <Panel>
            <SectionTitle>Invoice Status</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ResponsiveContainer width={110} height={110}>
                <PieChart>
                  <Pie data={[{ v: 780 }, { v: 300 }, { v: 120 }]} cx="50%" cy="50%"
                    innerRadius={32} outerRadius={50} startAngle={90} endAngle={-270} dataKey="v">
                    <Cell fill="#22c55e" /><Cell fill="#f59e0b" /><Cell fill="#ef4444" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                {[['#22c55e', 'Paid', '65% (780)'], ['#f59e0b', 'Pending', '25% (300)'], ['#ef4444', 'Overdue', '10% (120)']].map(([c, l, v]) => (
                  <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: '#64748b', flex: 1 }}>{l}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          {/* Transaction Status */}
          {/* <Panel>
            <SectionTitle>Transaction Status</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ResponsiveContainer width={110} height={110}>
                <PieChart>
                  <Pie data={[{ v: 22900 }, { v: 470 }, { v: 130 }]} cx="50%" cy="50%"
                    innerRadius={32} outerRadius={50} startAngle={90} endAngle={-270} dataKey="v">
                    <Cell fill="#22c55e" /><Cell fill="#ef4444" /><Cell fill="#f59e0b" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                {[['#22c55e', 'Success', '97.5% (22.9K)'], ['#ef4444', 'Failed', '2.0% (470)'], ['#f59e0b', 'Pending', '0.5% (130)']].map(([c, l, v]) => (
                  <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: '#64748b', flex: 1 }}>{l}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel> */}

          {/* MRR Overview */}
          <Panel>
            <SectionTitle>MRR Overview</SectionTitle>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{fmt(mrrData?.totalRevenue)}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Monthly Recurring Revenue</div>
            <span style={{ fontSize: 11, fontWeight: 600, color: mrrData?.isGrowthPositive ? '#22c55e' : '#ef4444', }}> {mrrData?.isGrowthPositive ? '↑' : '↓'} {mrrData?.growthPercentage}% vs last month</span>
            <ResponsiveContainer width="100%" height={70}>
              <AreaChart data={mrrData?.revenueData?.slice(-6)} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gMrr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip content={<ChartTipForGrowth currency />} />
                <Area type="monotone" dataKey="revenue" name="MRR" stroke="#6366f1" strokeWidth={2.5}
                  fill="url(#gMrr)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        </div>

        {/* ── Row 4: Tables ────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 360px', gap: 14 }}>

          {/* Recent Transactions */}
          <Panel style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Recent Transactions</span>
              {/* <span style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', cursor: 'pointer' }}>View All</span> */}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
                    {['ID', 'Tenant', 'Product', 'Amount', 'Status', 'Date'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#94a3b8', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentTransactionsData.map((row) => (
                    <tr
                      key={row.id}
                      style={{
                        borderBottom: "1px solid #f8fafc",
                      }}
                      onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        "#fafbfc")
                      }
                      onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        "transparent")
                      }
                    >
                      <td
                        style={{
                          padding: "9px 12px",
                          color: "#6366f1",
                          fontWeight: 600,
                          fontFamily: "monospace",
                        }}
                      >
                        {row.id}
                      </td>

                      <td
                        style={{
                          padding: "9px 12px",
                          color: "#0f172a",
                          maxWidth: 110,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.tenant}
                      </td>

                      <td
                        style={{
                          padding: "9px 12px",
                          color: "#475569",
                        }}
                      >
                        {row.product}
                      </td>

                      <td
                        style={{
                          padding: "9px 12px",
                          fontWeight: 600,
                          color: "#0f172a",
                        }}
                      >
                        {fmt(row.amount)}
                      </td>

                      <td style={{ padding: "9px 12px" }}>
                        <Badge status={row.status} />
                      </td>

                      <td
                        style={{
                          padding: "9px 12px",
                          color: "#94a3b8",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {/* Recent Invoices */}
          <Panel style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Recent Invoices</span>
              {/* <span style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', cursor: 'pointer' }}>View All</span> */}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
                    {['Invoice #', 'Tenant', 'Product', 'Amount', 'Status', 'Due Date'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#94a3b8', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentInvoicesData?.map((row) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #f8fafc' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '9px 12px', color: '#6366f1', fontWeight: 600, fontFamily: 'monospace' }}>{row.invoiceNumber}</td>
                      <td style={{ padding: '9px 12px', color: '#0f172a', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.tenant}</td>
                      <td style={{ padding: '9px 12px', color: '#475569' }}>{row.product}</td>
                      <td style={{ padding: '9px 12px', fontWeight: 600, color: '#0f172a' }}>{fmt(row.amount)}</td>
                      <td style={{ padding: '9px 12px' }}><Badge status={row.status} /></td>
                      <td style={{ padding: '9px 12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{row.dueDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {/* Top Products */}
          <Panel style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Top Products by Revenue</span>
              {/* <span style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', cursor: 'pointer' }}>View All</span> */}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
                    {['Product', 'Revenue', '% Share', 'Growth'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#94a3b8' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topProductsData.map((p, i) => (
                    <tr key={p.name} style={{ borderBottom: '1px solid #f8fafc' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 9, height: 9, borderRadius: '50%', background: DONUT_COLORS[i], flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, color: '#0f172a' }}>{p.name}</span>
                        </span>
                      </td>
                      <td style={{ padding: '9px 12px', fontWeight: 600, color: '#0f172a' }}>{fmt(p.revenue)}</td>
                      <td style={{ padding: '9px 12px', color: '#475569' }}>{p.share}%</td>
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 12 }}>↑ {p.growth}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

        </div>
      </main>
    </div>
  );
};

export default MasterDashboard;
